import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { Pressable } from "@gonoodle/gn-universe-ui";
import { AnimatePresence, motion } from "framer-motion";
import { twMerge } from "tailwind-merge";

import { usePlayer } from "./hooks";

const Video = React.forwardRef(
  ({ options, children, onDisplayClick, onVideoStart }, forwardedRef) => {
    const [toast, setToast] = React.useState(null);
    const id = React.useId();
    const player = usePlayer();

    const { source, disabled = false } = options;

    React.useEffect(() => {
      player.load(source);
    }, [source, player]);

    React.useEffect(() => {
      let unsubscribe = () => {};
      if (player.onDisplayClick) {
        unsubscribe = player.onDisplayClick(onDisplayClick);
      }
      return () => {
        if (player.isReady) {
          unsubscribe();
        }
      };
    }, [onDisplayClick, player]);

    React.useEffect(() => {
      if (player.isReady) {
        player.on("play", onVideoStart);
      }

      return () => {
        if (player.isReady) {
          player.off("play", onVideoStart);
        }
      };
    }, [onVideoStart, player, player.isReady]);

    React.useImperativeHandle(forwardedRef, () => ({
      showToast: (t) => {
        setToast(t);
      },
    }));

    // We need to get the video player div using native selectors instead of using player.containerRef because jwPlayer
    // uses direct DOM manipulations on it, so react has no way to know about those changes
    const jwPlayer =
      typeof document !== "undefined"
        ? document.getElementById(id)?.getElementsByClassName("jwplayer")[0]
        : null;

    return (
      <div
        className={`relative gonoodle-jwplayer-container ${
          player?.hideControls ? "gonoodle-jwplayer-no-controls" : ""
        }`}
        id={id}
      >
        <div
          className="w-full h-full aspect-w-16 aspect-h-9 bg-black"
          ref={player.containerRef}
        />
        <Pressable
          onPress={onDisplayClick}
          className={twMerge(
            "pointer-events-none",
            (disabled || player.hideControls) &&
              "absolute inset-0 overflow-hidden",
            disabled && "cursor-default pointer-events-auto",
          )}
          data-testid="playAttemptBtn"
        >
          {children}
        </Pressable>
        {player.isReady && jwPlayer && player?.player.getFullscreen()
          ? // These toast notifications are rendered in a portal because they need to be a child of the video player
            // container (i.e. `player.containerRef`), otherwise they won't be visible when the video is full screen.
            ReactDOM.createPortal(
              <div role="region" aria-label="Notifications">
                <ol className="fixed top-6 right-6 ml-6">
                  <AnimatePresence>
                    {toast ? (
                      <motion.li
                        role="status"
                        className="gn-text-sm bg-gray-900 text-white rounded-lg max-w-full animate-slide-left"
                        initial={{ x: 100 }}
                        animate={{ x: 0 }}
                        exit={{ opacity: 0 }}
                      >
                        {toast}
                      </motion.li>
                    ) : null}
                  </AnimatePresence>
                </ol>
              </div>,
              jwPlayer,
            )
          : null}
      </div>
    );
  },
);

Video.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  options: PropTypes.shape({
    source: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
  }).isRequired,
  onDisplayClick: PropTypes.func,
  onVideoStart: PropTypes.func,
};

Video.defaultProps = {
  children: null,
  onDisplayClick: () => {},
  onVideoStart: () => {},
};

export default Video;
